/**
 * Contrats du module « ecole »: ce qu'est une question, une serie, un resultat.
 *
 * Tout est du code PUR: aucun DOM, aucun three.js, aucun node, aucun
 * `Math.random`, aucun `Date.now`. Le hasard arrive par une graine, le temps
 * arrive en argument. C'est ce qui rend le generateur testable sans navigateur
 * et reproductible d'une machine a l'autre.
 *
 * Les identifiants et les commentaires sont en francais SANS accents; tout ce
 * que l'enfant lit (`enonce`, `consigne`, `choix`, `reponses`) est du francais
 * AVEC ses accents. Un jeu de grammaire qui contient une faute est pire que
 * pas de jeu du tout.
 */

/** Annees scolaires couvertes: de la 1re a la 6e. */
export type Niveau = 1 | 2 | 3 | 4 | 5 | 6

export const NIVEAUX: readonly Niveau[] = [1, 2, 3, 4, 5, 6]

/** Nom affiche d'un niveau. */
export function nomNiveau(n: Niveau): string {
  return n === 1 ? '1re année' : `${n}e année`
}

/** Les deux matieres. Pas d'`enum`: `erasableSyntaxOnly` l'interdit. */
export const MATIERES = { maths: 'maths', francais: 'francais' } as const
export type Matiere = (typeof MATIERES)[keyof typeof MATIERES]

/**
 * Les sujets. Le « defi chronometre » n'en est PAS un: c'est une couche qui se
 * pose par-dessus n'importe quel sujet (voir `OptionsSerie.chrono`).
 */
export type Sujet =
  | 'arithmetique'
  | 'geometrie'
  | 'problemes'
  | 'conjugaison'
  | 'homophones'
  | 'accords'
  | 'vocabulaire'

export interface SujetInfo {
  id: Sujet
  matiere: Matiere
  /** Nom affiche, accentue. */
  nom: string
  icone: string
  /** Une ligne d'explication, affichee sous le nom. */
  resume: string
}

export const SUJETS: readonly SujetInfo[] = [
  {
    id: 'arithmetique',
    matiere: 'maths',
    nom: 'Calcul',
    icone: '➕',
    resume: 'Additions, soustractions, tables, fractions, décimaux, pourcentages',
  },
  {
    id: 'geometrie',
    matiere: 'maths',
    nom: 'Géométrie et mesure',
    icone: '📐',
    resume: 'Formes, périmètre, aire, angles, conversions',
  },
  {
    id: 'problemes',
    matiere: 'maths',
    nom: 'Problèmes écrits',
    icone: '🧩',
    resume: 'De petites histoires à résoudre',
  },
  {
    id: 'conjugaison',
    matiere: 'francais',
    nom: 'Conjugaison',
    icone: '🕰️',
    resume: 'Présent, imparfait, futur, passé composé',
  },
  {
    id: 'homophones',
    matiere: 'francais',
    nom: 'Homophones',
    icone: '👯',
    resume: 'a ou à, son ou sont, ces ou ses, où ou ou…',
  },
  {
    id: 'accords',
    matiere: 'francais',
    nom: 'Accords',
    icone: '🔗',
    resume: 'Genre, nombre, participe passé',
  },
  {
    id: 'vocabulaire',
    matiere: 'francais',
    nom: 'Vocabulaire et orthographe',
    icone: '📖',
    resume: 'Contraires, synonymes, pluriels, mots à retenir',
  },
]

export function sujetInfo(id: Sujet): SujetInfo {
  const trouve = SUJETS.find((s) => s.id === id)
  // La liste couvre l'union complete: ce filet ne sert qu'a satisfaire le type.
  return trouve ?? (SUJETS[0] as SujetInfo)
}

export function sujetsDe(m: Matiere): readonly Sujet[] {
  return SUJETS.filter((s) => s.matiere === m).map((s) => s.id)
}

/**
 * Ou l'on joue. Le portable du bureau ne sert qu'aux maths et a la
 * calculatrice; l'ecole ouvre tout.
 */
export const MODES = { portable: 'portable', ecole: 'ecole' } as const
export type ModeEcole = (typeof MODES)[keyof typeof MODES]

/** Les sujets accessibles selon l'endroit ou l'on ouvre le module. */
export function sujetsDuMode(mode: ModeEcole): readonly Sujet[] {
  return mode === 'portable' ? sujetsDe('maths') : SUJETS.map((s) => s.id)
}

/**
 * Comment l'enfant repond.
 *
 * `nombre`: un pave numerique de vrais boutons. JAMAIS un `<input type=number>`:
 * le clavier du systeme mange la moitie d'un ecran de telephone.
 * `choix`: une liste de boutons.
 */
export type SaisieQuestion = 'nombre' | 'choix'

/**
 * Une question est une DONNEE, pas du code: on peut la serialiser, la relire
 * dans un journal, la comparer d'une execution a l'autre.
 */
export interface Question {
  /** Stable pour une graine et des options donnees. */
  id: string
  sujet: Sujet
  niveau: Niveau
  /** Le genre d'exercice (voir la table de progression). */
  activite: string
  /** Ce que l'enfant lit. */
  enonce: string
  /** La consigne courte, au-dessus de la zone de reponse. */
  consigne: string
  saisie: SaisieQuestion
  /** Les boutons proposes quand `saisie === 'choix'`. */
  choix?: readonly string[]
  /** Formes acceptees. `reponses[0]` est celle qu'on montre en correction. */
  reponses: readonly string[]
  /** Unite affichee a cote de la reponse (cm, cm², $, °…). */
  unite?: string
  /**
   * Difficulte. Vaut `niveau * 100 + rang * 10 + taille`, donc TOUTE question
   * d'un niveau est strictement plus difficile que TOUTE question du niveau
   * precedent. C'est la propriete que verrouille tests/unit/ecole.test.ts.
   */
  poids: number
}

export interface OptionsSerie {
  niveau: Niveau
  sujets: readonly Sujet[]
  /** Combien de questions dans la serie. */
  nombre: number
  graine: number
  /** Le defi chronometre: une couche par-dessus n'importe quel sujet. */
  chrono: boolean
}

export interface Serie {
  options: OptionsSerie
  questions: readonly Question[]
  /** Temps accorde pour toute la serie, en secondes. 0 quand il n'y a pas de chrono. */
  secondes: number
}

// ------------------------------------------------------------------ resultat

/** Une reponse donnee, telle qu'elle part vers le serveur et le journal. */
export interface ReponseDonnee {
  id: string
  sujet: Sujet
  activite: string
  enonce: string
  /** Ce que l'enfant a repondu. Vide s'il n'a pas eu le temps. */
  donnee: string
  /** La reponse attendue, sous sa forme d'affichage. */
  attendue: string
  juste: boolean
  /** Temps de reflexion, en millisecondes. */
  ms: number
}

/**
 * Ce que le panneau emet a la fin d'une serie, via `onPartieFinie`.
 * Structure PLATE et serialisable en JSON: le serveur n'a qu'a la ranger.
 */
export interface ResultatPartie {
  /** Numero de forme. A incrementer si la structure change. */
  version: 1
  mode: ModeEcole
  matiere: Matiere
  niveau: Niveau
  sujets: readonly Sujet[]
  graine: number
  chrono: boolean
  /** Questions POSEES (le chrono peut en couper la fin). */
  total: number
  justes: number
  /** Meilleure suite de bonnes reponses. */
  meilleureSerie: number
  /** 0 a 100, arrondi. */
  pourcentage: number
  /** 0 a 3, la recompense visible. */
  etoiles: number
  /** Points a convertir en objets du jeu par le serveur. */
  points: number
  /** Duree totale de la partie, en millisecondes. */
  msTotal: number
  reponses: readonly ReponseDonnee[]
}

/**
 * Progres cumules d'UN compte. Le serveur garde ceci; le client ne l'ecrit
 * jamais lui-meme (il n'emet que des `ResultatPartie`).
 */
export interface ProgresEcole {
  version: 1
  /** Points gagnes depuis toujours. */
  points: number
  parties: number
  questions: number
  justes: number
  /** Meilleur pourcentage obtenu, par niveau. Clefs: '1'..'6'. */
  meilleurParNiveau: Record<string, number>
  /** Nombre de bonnes reponses par sujet. Clefs: les `Sujet`. */
  justesParSujet: Record<string, number>
  /** Nombre de questions posees par sujet. */
  posesParSujet: Record<string, number>
  /** Points deja echanges contre des objets, pour ne pas donner deux fois. */
  pointsDepenses: number
}

export function progresVide(): ProgresEcole {
  return {
    version: 1,
    points: 0,
    parties: 0,
    questions: 0,
    justes: 0,
    meilleurParNiveau: {},
    justesParSujet: {},
    posesParSujet: {},
    pointsDepenses: 0,
  }
}
